// react imports
import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { InputNumber, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import axios from "axios";

// common imports
import { API_URL } from "common/constants/config";

import CategoryHeader from "./CategoryHeader";
import "./css/Pagination.css";
import "./css/Table.css";
import { RESIDUE_TYPE_NAMES } from "./constants/residues";

function Residues() {
  type ResidueTypeName =
    | "rest"
    | "glass"
    | "organic"
    | "plastic"
    | "paperAndCardboard";

  interface Residue {
    residueType: string;
    wastePercentage: number;
    co2EmissionPerKg: number;
  }

  interface ResidueData extends Residue {
    key: string;
  }

  interface UpdatePayloadValues {
    residueTypes: {
      [residue: string]: {
        wastePercentage: number;
        co2EmissionPerKg: number;
      };
    };
  }

  const [residues, setResidues] = useState<ResidueData[]>([]);
  const { control, handleSubmit } = useForm();

  const columns: ColumnsType<ResidueData> = [
    {
      title: "Tipos de residuos",
      key: "residueType",
      dataIndex: "residueType",
      align: "center",
      width: 209,
    },
    {
      title: "Porcentaje",
      key: "wastePercentage",
      dataIndex: "wastePercentage",
      align: "center",
      width: 219,
      render: (value, record) => (
        <Controller
          name={`residueTypes.${record.key}.wastePercentage`}
          control={control}
          defaultValue={value}
          render={({ field }) => (
            <InputNumber
              {...field}
              formatter={(val) => `${val}%`}
              parser={(str) => {
                if (str) return Number(str.replace("%", ""));
              }}
              min={0}
              max={100}
              decimalSeparator=","
              onPressEnter={handlePressEnter}
            />
          )}
        />
      ),
    },
    {
      title: "Valor",
      key: "co2EmissionPerKg",
      dataIndex: "co2EmissionPerKg",
      align: "center",
      width: 231,
      render: (value, record) => (
        <Controller
          name={`residueTypes.${record.key}.co2EmissionPerKg`}
          control={control}
          defaultValue={value}
          render={({ field }) => (
            <InputNumber
              {...field}
              decimalSeparator=","
              onPressEnter={handlePressEnter}
            />
          )}
        />
      ),
    },
  ];

  function handlePressEnter(e: React.KeyboardEvent<HTMLInputElement>) {
    e.preventDefault();
  }

  async function onSubmit(values: UpdatePayloadValues) {
    const payload = {
      residues: values,
    };

    await axios.put("/admin/calculator/householdHabits", payload, {
      baseURL: API_URL,
    });
  }

  async function fetchResidues() {
    const {
      data: {
        householdHabits: {
          residues: { residueTypes },
        },
      },
    } = await axios.get(`${API_URL}/admin/calculator/household-habits`);

    const residueEntries = Object.entries<Omit<Residue, "name">>(residueTypes);
    const mappedResidues: ResidueData[] = residueEntries.map(
      ([residueType, entry]) => ({
        key: residueType,
        residueType: RESIDUE_TYPE_NAMES[residueType as ResidueTypeName],
        wastePercentage: entry.wastePercentage,
        co2EmissionPerKg: entry.co2EmissionPerKg,
      })
    );
    setResidues(mappedResidues);
  }

  useEffect(() => {
    fetchResidues();
  }, []); // eslint-disable-line

  return (
    <>
      <CategoryHeader title="Residuos" formName="residues" />
      <form id="residues" onSubmit={handleSubmit(onSubmit)}>
        <Table dataSource={residues} columns={columns} pagination={false} />
      </form>
    </>
  );
}

export default Residues;
